import React from 'react';
import { ArrowLeft, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { EventPost, EventMetadata, EventMode } from '@feature/event-detail/types';
import { formatEventTime } from '@feature/event-detail/utils/formatEventTime';
import { useIsMobile } from '@/hooks/useIsMobile';

type StatusType = 'live' | 'ended';

interface Props {
  event: EventPost;
  metadata: EventMetadata;
  mode: EventMode;
  status?: StatusType;
  onInfoPress?: () => void;
}

export function EventHeader({ event, metadata, mode, status, onInfoPress }: Props): React.ReactElement {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const timeDisplay = formatEventTime(event.starts_at, event.ends_at);
  const metaText = `${timeDisplay} • ${metadata.attendee_count} joined`;

  const showOpenInAppButton = mode === 'before';
  const showInfoButton = mode === 'during';

  const handleBack = () => {
    navigate(-1);
  };

  const handleOpenInApp = () => {
    window.location.href = '/download';
  };

  return (
    <div style={styles.header}>
      {isMobile && (
        <div style={styles.backButtonContainer}>
          <button
            style={styles.backButton}
            onClick={handleBack}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f0f0f0';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <ArrowLeft size={24} color="#333" />
          </button>
        </div>
      )}
      {!isMobile && <div style={styles.backButtonContainer} />}

      <div style={styles.titleContainer}>
        <h1 style={styles.title}>{event.title}</h1>
        {metaText && (
          <div style={styles.metaRow}>
            <span style={styles.metaText}>{metaText}</span>
            {showInfoButton && onInfoPress && (
              <button style={styles.infoButton} onClick={onInfoPress}>
                <Info size={16} color="#999" />
              </button>
            )}
          </div>
        )}
      </div>

      <div style={styles.rightContainer}>
        {showOpenInAppButton && (
          <button
            style={styles.openInAppButton}
            onClick={handleOpenInApp}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#2563eb';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#3b82f6';
            }}
          >
            Open in
            <br />
            App
          </button>
        )}
        {status === 'ended' && <span style={styles.endedIndicator}>ENDED</span>}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: '16px',
    paddingRight: '16px',
    paddingTop: '8px',
    paddingBottom: '8px',
    borderBottom: '1px solid #e0e0e0',
  },
  backButtonContainer: {
    width: '80px',
  },
  backButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    padding: '8px',
    borderRadius: '8px',
    transition: 'background-color 0.2s',
  },
  titleContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  title: {
    color: '#666',
    fontSize: '16px',
    fontWeight: 'bold',
    textAlign: 'center',
    margin: 0,
  },
  metaRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  metaText: {
    color: '#999',
    fontSize: '12px',
    marginTop: '2px',
    textAlign: 'center',
  },
  infoButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: '4px',
    marginTop: '1px',
    padding: '2px',
  },
  rightContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    minWidth: '80px',
  },
  openInAppButton: {
    backgroundColor: '#3b82f6',
    color: '#fff',
    fontSize: '11px',
    fontWeight: 600,
    paddingTop: '4px',
    paddingBottom: '4px',
    paddingLeft: '8px',
    paddingRight: '8px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    lineHeight: '1.2',
  },
  endedIndicator: {
    color: '#6b7280',
    fontSize: '14px',
    fontWeight: 'bold',
  },
};
